import {
	PriorityBadgeValueMapping,
	SourcingRequestStatus,
	SourcingRequestStatusLabels,
} from '@supply-mover-2-0/shared';
import type { KanbanStaticColumn, ListBuilderList } from '../../../types';
import { createSourcingRequestColumns } from '../columns';
import {
	SOURCING_REQUEST_ACTIONS,
	SOURCING_REQUEST_CLICK_ACTION,
} from '../shared';

/**
 * Kanban column order for the SR pipeline. Draft is leftmost (per locked
 * Draft-UX decision), then the active workflow states, with Cancelled at
 * the right edge as a terminal lane. Labels come from the shared enum so
 * any future relabel updates both list filter pickers and the kanban
 * header in one place.
 */
const SOURCING_REQUEST_KANBAN_COLUMNS: ReadonlyArray<KanbanStaticColumn> = [
	{
		id: SourcingRequestStatus.DRAFT,
		label: SourcingRequestStatusLabels[SourcingRequestStatus.DRAFT],
		ordinal: 1,
	},
	{
		id: SourcingRequestStatus.OPEN,
		label: SourcingRequestStatusLabels[SourcingRequestStatus.OPEN],
		ordinal: 2,
	},
	{
		id: SourcingRequestStatus.IN_PROGRESS,
		label: SourcingRequestStatusLabels[SourcingRequestStatus.IN_PROGRESS],
		ordinal: 3,
	},
	{
		id: SourcingRequestStatus.PARTIALLY_FULFILLED,
		label:
			SourcingRequestStatusLabels[SourcingRequestStatus.PARTIALLY_FULFILLED],
		ordinal: 4,
	},
	{
		id: SourcingRequestStatus.FULFILLED,
		label: SourcingRequestStatusLabels[SourcingRequestStatus.FULFILLED],
		ordinal: 5,
	},
	{
		id: SourcingRequestStatus.CANCELLED,
		label: SourcingRequestStatusLabels[SourcingRequestStatus.CANCELLED],
		ordinal: 6,
	},
];

/**
 * All Sourcing Requests list configuration
 * Base list that other lists inherit from
 */
export const allRequestsList: ListBuilderList = {
	id: 'default-sourcing-requests-all',
	cacheTTL: 300000,
	name: 'All',
	type: 'default',
	active: true,
	sortOrder: 0,
	filterRules: {
		sourcingRequestAssignedUser: {
			defaultValue: [{ id: 'ME', label: 'Me' }],
			// PD-14671: "All" was identical to "My Requests" — the ME
			// default applied to everyone, so a manager's All tab silently
			// showed only their own SRs and the all-count lied. The ME
			// default is now RBAC scoping ONLY: it applies (locked+hidden)
			// for users without view-any, and users WITH view-any get a
			// genuinely unfiltered All. "My Requests" stays the Me-scoped
			// view for everyone.
			applyDefaultIf: {
				type: 'group',
				joiner: 'AND',
				negated: false,
				children: [
					{ type: 'rule', action: 'sourcing_requests:view:any', negated: true },
				],
			},
			lockedIf: {
				type: 'group',
				joiner: 'AND',
				negated: false,
				children: [
					{ type: 'rule', action: 'sourcing_requests:view:any', negated: true },
				],
			},
			hiddenIf: {
				type: 'group',
				joiner: 'AND',
				negated: false,
				children: [
					{ type: 'rule', action: 'sourcing_requests:view:any', negated: true },
				],
			},
		},
	},
	columns: createSourcingRequestColumns(),
	viewMode: 'table',
	defaultSortId: 'column-sr-priority-desc',
	clickAction: SOURCING_REQUEST_CLICK_ACTION,
	actions: SOURCING_REQUEST_ACTIONS,
	bulkActionTemplate: 'sourcing-requests',
	// Kanban grouped by SR master status. Columns are statically defined
	// (Draft → Open → InProgress → PartiallyFulfilled → Fulfilled → Cancelled)
	// so all six render even when empty, and headers show the human-readable
	// labels from SourcingRequestStatusLabels rather than raw enum values.
	// Drag-between-columns is disabled because status is derived from line
	// state (see deriveSourcingRequestStatus in
	// libs/core/src/utils/sourcingRequestStatus.ts); the only manual override
	// is Cancel, which lives in the row action menu, not as a drop target.
	kanbanConfig: {
		groupByField: {
			fieldName: 'status',
			objectTable: 'sourcing_request',
		},
		staticColumns: SOURCING_REQUEST_KANBAN_COLUMNS,
		// Per-column loading: each column independently queries up to
		// KANBAN_ITEMS_PER_COLUMN cards filtered by sourcingRequestStatus
		// (matches the static column id), so a busy "In Progress" column
		// doesn't starve "Fulfilled" of rows behind a single page-1 cap.
		filterKey: 'sourcingRequestStatus',
		allowDragBetweenColumns: false,
		cardLayout: {
			sections: {
				header: [
					{
						id: 'card-sr-name',
						link: {
							type: 'object',
							enabled: true,
							objectTable: 'sourcing_request',
						},
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'name',
								objectTable: 'sourcing_request',
							},
							defaultValue: '-',
						},
						styleConfig: {
							size: 'sm',
							colorRule: { type: 'default' },
							fontWeight: 'semibold',
						},
						displayStyle: 'text',
					},
					{
						// Pending-info chip — only renders when at least one
						// line on this SR is parked in `pending_info` waiting
						// for a clarification from the requestor.
						// `conditionalVisibility` drops it for SRs with no
						// blocked lines so the kanban stays uncluttered.
						// Amber color matches the line-card "Awaiting
						// clarification" banner so the visual language is
						// consistent across the app.
						id: 'card-pending-info',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'linesPendingInfo',
								objectTable: 'sourcing_request',
							},
						},
						styleConfig: {
							size: 'xs',
							colorRule: {
								type: 'static',
								staticColor: {
									bg: '#fef3c7',
									text: '#92400e',
								},
							},
						},
						displayStyle: 'badge',
						prefix: '? ',
						conditionalVisibility: {
							field: 'linesPendingInfo',
							condition: 'not_equals',
							value: 0,
						},
					},
				],
				body: [
					{
						id: 'card-client',
						dataConfig: {
							type: 'field',
							field: { fieldName: 'name', objectTable: 'client' },
							defaultValue: '-',
						},
						styleConfig: {
							size: 'xs',
							colorRule: { type: 'default' },
						},
						displayStyle: 'text',
					},
					{
						id: 'card-priority',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'priority',
								objectTable: 'sourcing_request',
							},
							defaultValue: '-',
						},
						styleConfig: {
							size: 'xs',
							colorRule: { type: 'default' },
							// Static label + color per priority level so the card
							// surfaces "Urgent" / "High" / "Normal" / "Low" with the
							// calm-green → red ramp instead of the raw enum value.
							valueMapping: [...PriorityBadgeValueMapping],
						},
						displayStyle: 'badge',
					},
				],
				footer: [
					{
						id: 'card-line-progress',
						label: 'Lines',
						dataConfig: {
							type: 'template',
							template: '{linesFulfilled}/{totalLines}',
							templateFields: [
								{ fieldName: 'linesFulfilled' },
								{ fieldName: 'totalLines' },
							],
						},
						styleConfig: {
							size: 'xs',
							colorRule: { type: 'default' },
						},
						displayStyle: 'text',
					},
					{
						// Estimated spend roll-up (sum of line.estimatedCost *
						// line.quantityRequested for non-cancelled lines).
						// Stored on the SR header by `updateLineCounts` so it's
						// available to the kanban without per-card joins.
						id: 'card-total-cost',
						label: 'Est. Cost',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'totalEstimatedCost',
								objectTable: 'sourcing_request',
							},
							defaultValue: '—',
						},
						styleConfig: {
							size: 'xs',
							colorRule: { type: 'default' },
							formatOptions: { currency: 'USD' },
						},
						displayStyle: 'currency',
					},
					{
						id: 'card-target-date',
						label: 'Target',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'targetDate',
								objectTable: 'sourcing_request',
							},
						},
						styleConfig: {
							size: 'xs',
							colorRule: { type: 'default' },
							formatOptions: { dateFormat: 'short' },
						},
						displayStyle: 'date',
					},
				],
			},
		},
		headerAggregations: [{ type: 'count', label: 'Count' }],
	},
};
